import { Box, Grid } from '@material-ui/core';
import { GridItemsAlignment, GridJustification, GridSize } from '@material-ui/core/Grid';
import React from 'react';
import { EJRecordTypes } from 'src/common/enumerations/ej-record-types';
import { convertCurrency } from 'src/common/helpers/currency-helper';
import { IElectronicJournalRecord } from 'src/common/interfaces/states/IElectronicJournalRecord';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import VerticalCenter from 'src/vertical-center/vertical-center';
import { ReactComponent as PromotionIcon } from '../../common/media/Promotions.svg';
import { getProductCount } from '../business-logic/product-count';
import { styles } from './total-styles';
import { useTranslation } from 'react-i18next';

const Total: React.FC<TotalProps> = (props: TotalProps): JSX.Element => {
  const classes = styles();
  const { t } = useTranslation();
  const trainingModeClasses = trainingModeStyles();
  const hasReturnItem = props.ejItems.filter(i => i.Type === EJRecordTypes.ReturnItem).length > 0;
  const subtotalText = t('ItemDetailsView_SubTotal');
  const taxText = t('ReceiptView_Tax');

  const itemsTotalProperty = (
    <TotalProperty
      label={
        props.returnModeActive || hasReturnItem
          ? t('ItemDetailsView_ReturnItems').toUpperCase()
          : t('items').toUpperCase()
      }
      value={getProductCount(props.ejItems, props.returnModeActive).toString()}
      classes={{
        left: props.isCustomerDisplay ? classes.totalLeftCustomer : classes.totalLeft,
        right: classes.totalRight,
      }}
      columns={{
        left: props.returnModeActive ? 9 : 7,
        right: props.returnModeActive ? 3 : 5,
      }}
      dataTestID='items-total'
    />
  );

  const itemsSubTotalProperty = (
    <TotalProperty
      label={props.isCustomerDisplay ? subtotalText : subtotalText.toUpperCase()}
      value={convertCurrency(props.subTotal, props.currencyCode)}
      classes={{
        left: props.isCustomerDisplay ? classes.totalLeftCustomer : classes.totalLeft,
        right: classes.totalRight,
      }}
      dataTestID='items-subtotal'
    />
  );

  const itemsSavingsProperty = !props.returnModeActive ? (
    <TotalProperty
      label={
        props.isCustomerDisplay
          ? t('ReceiptView_YourSavings').toUpperCase()
          : t('ItemDetailsView_Savings').toUpperCase()
      }
      value={convertCurrency(props.savings, props.currencyCode)}
      classes={{
        left: props.isCustomerDisplay ? classes.totalLeftCustomer : classes.totalLeft,
        right: classes.totalRight,
      }}
      columns={{ left: 7, right: 5 }}
      dataTestID='items-savings'
    />
  ) : (
    <></>
  );

  const itemsTaxProperty = (
    <TotalProperty
      label={props.isCustomerDisplay ? taxText : taxText.toUpperCase()}
      value={convertCurrency(props.tax, props.currencyCode)}
      classes={{
        left: props.isCustomerDisplay ? classes.totalLeftCustomer : classes.totalLeft,
        right: classes.totalRight,
      }}
      dataTestID='items-tax'
    />
  );

  const cashierEJ = (
    <>
      <Box className={classes.totalRow}>
        <Grid container>
          <Grid item className={`${classes.totalRowItem} ${classes.totalRowLeftItem}`}>
            {itemsTotalProperty}
          </Grid>
          <Grid item className={classes.totalRowItem}>
            {itemsSubTotalProperty}
          </Grid>
        </Grid>
      </Box>
      <Box>
        <Grid container>
          <Grid item className={`${classes.totalRowItem} ${classes.totalRowLeftItem}`}>
            {itemsSavingsProperty}
          </Grid>
          <Grid item className={classes.totalRowItem}>
            {itemsTaxProperty}
          </Grid>
        </Grid>
      </Box>
    </>
  );

  const customerEJ = (
    <Grid container>
      <Grid container wrap='nowrap' alignItems='center'>
        <div className={classes.customerTotalListItem}></div>
        {itemsSubTotalProperty}
      </Grid>
      <Grid container wrap='nowrap' alignItems='center'>
        <div className={classes.customerTotalListItem}></div>
        {itemsTaxProperty}
      </Grid>
      <Grid container wrap='nowrap' alignItems='center' className={classes.customerSavings}>
        <div className={classes.customerTotalListItem}>
          {!props.returnModeActive ? <PromotionIcon /> : <></>}
        </div>
        {itemsSavingsProperty}
      </Grid>
    </Grid>
  );

  return (
    <Grid container direction='column' className={`${classes.outerContainer}`}>
      <Grid item className={`fullWidth ${classes.totalItem}`}>
        <Grid
          container
          className={`${classes.container} ${
            props.trainingModeActive ? trainingModeClasses.trainingModeBackground : ''
          }`}
          data-testid='total-inner-container'
        >
          <Grid
            item
            className={`flexFull ${classes.totalContent} ${
              props.isCustomerDisplay ? classes.totalContentCustomer : ''
            }`}
          >
            <VerticalCenter itemClass='fullWidth'>
              {props.isCustomerDisplay ? customerEJ : cashierEJ}
            </VerticalCenter>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

interface TotalProps {
  subTotal: number;
  tax: number;
  currencyCode: string;
  savings: number;
  ejItems: IElectronicJournalRecord[];
  trainingModeActive: boolean;
  returnModeActive: boolean;
  isCustomerDisplay?: boolean;
}

export default Total;

interface ITotalProperty {
  label: string;
  value: string;
  classes?: { container?: string; left?: string; right?: string; imageInvisible?: string };
  columns?: { left?: GridSize; right?: GridSize };
  justify?: GridJustification;
  alignItems?: GridItemsAlignment;
  dataTestID?: string;
}

export const TotalProperty: React.FC<ITotalProperty> = ({
  label,
  value,
  classes,
  justify,
  alignItems,
  dataTestID,
}): JSX.Element => {
  return (
    <Grid
      container
      justifyContent={justify || 'space-between'}
      alignItems={alignItems}
      className={classes ? classes.container : undefined}
      data-testid={dataTestID}
      wrap='nowrap'
    >
      <Grid item data-testid={`${dataTestID}-label`}>
        {label}
      </Grid>
      <Grid
        item
        className={classes ? classes.right : undefined}
        data-testid={`${dataTestID}-value`}
      >
        {value}
      </Grid>
    </Grid>
  );
};
